import React, { useState, useEffect, useRef } from 'react';
import {
  FaRocket,
  FaEye,
  FaHeart,
  FaUsers,
  FaChartLine,
  FaAward,
  FaHandshake,
  FaBalanceScale,
  FaGlobeAmericas,
  FaLightbulb,
  FaArrowRight,
  FaQuoteLeft,
  FaStar,
  FaChevronLeft,
  FaChevronRight,
  FaLeaf,
  FaRecycle,
  FaSeedling,
  FaWater
} from 'react-icons/fa';

const OurMissionAndVision = () => {
  const [activeTab, setActiveTab] = useState('mission');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [animatedValues, setAnimatedValues] = useState({
    companies: 0,
    employees: 0,
    countries: 0,
    satisfaction: 0
  });
  const sectionRef = useRef(null);

  const missionVisionData = {
    mission: {
      title: "Our Mission",
      subtitle: "Democratizing HR Excellence for Every Organization",
      description: "To empower organizations of all sizes with intelligent, accessible, and human-centric HR solutions that transform how they attract, develop, and retain exceptional talent.",
      icon: <FaRocket />,
      color: "#4a90e2",
      features: [
        {
          icon: <FaUsers />,
          title: "Accessibility First",
          description: "Making enterprise-grade HR tools accessible to startups and SMBs"
        },
        {
          icon: <FaChartLine />,
          title: "Data-Driven Decisions",
          description: "Leveraging AI to provide actionable insights for people management"
        },
        {
          icon: <FaHandshake />,
          title: "Human-Centric Design",
          description: "Keeping the human experience at the core of all our solutions"
        },
        {
          icon: <FaGlobeAmericas />,
          title: "Global Impact",
          description: "Supporting diverse workforces across different cultures and regions"
        }
      ]
    },
    vision: {
      title: "Our Vision",
      subtitle: "Shaping the Future of Work Through Intelligent HR",
      description: "To create a world where every organization thrives through empowered, engaged, and fulfilled employees, enabled by seamless HR technology.",
      icon: <FaEye />,
      color: "#667eea",
      features: [
        {
          icon: <FaLightbulb />,
          title: "Innovation Leadership",
          description: "Pioneering the next generation of HR technology and practices"
        },
        {
          icon: <FaAward />,
          title: "Excellence Standard",
          description: "Setting new benchmarks for HR excellence and employee experience"
        },
        {
          icon: <FaBalanceScale />,
          title: "Ethical AI",
          description: "Ensuring fair, transparent, and ethical use of AI in HR processes"
        },
        {
          icon: <FaHeart />,
          title: "People First Culture",
          description: "Fostering workplaces where people feel valued and empowered"
        }
      ]
    }
  };

  const testimonials = [
    {
      name: "David Kim",
      company: "TechGrowth Inc.",
      role: "HR Director",
      content: "HR Copilot transformed our hiring process. We reduced time-to-hire by 60% while improving candidate quality significantly.",
      rating: 5
    },
    {
      name: "Maria Garcia",
      company: "InnovateLabs",
      role: "People Operations",
      content: "The onboarding experience we've created with HR Copilot has increased our 90-day retention by 45%. Truly remarkable.",
      rating: 5
    },
    {
      name: "James Wilson",
      company: "Global Solutions",
      role: "Talent Acquisition Lead",
      content: "Finally, an HR platform that understands the needs of modern, distributed teams. The analytics alone are worth it.",
      rating: 5
    }
  ];

  const environmentalMissions = [
    { icon: <FaLeaf />, title: "Go green revolution", color: "#10b981" },
    { icon: <FaRecycle />, title: "Complete Sustainability", color: "#3b82f6" },
    { icon: <FaSeedling />, title: "Protect Innovation", color: "#f59e0b" },
    { icon: <FaWater />, title: "Volunteer Possibility", color: "#06b6d4" },
    { icon: <FaGlobeAmericas />, title: "Competitive thinking", color: "#8b5cf6" },
    { icon: <FaHeart />, title: "No grant violation", color: "#ec4899" }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const targetValues = {
            companies: 10000,
            employees: 2500000,
            countries: 50,
            satisfaction: 86
          };

          const duration = 2000;
          const steps = 60;
          const stepValues = {};
          
          Object.keys(targetValues).forEach(key => {
            stepValues[key] = targetValues[key] / steps;
          });

          let currentStep = 0;
          const timer = setInterval(() => {
            currentStep++;
            setAnimatedValues(prev => ({
              companies: Math.min(Math.floor(prev.companies + stepValues.companies), targetValues.companies),
              employees: Math.min(Math.floor(prev.employees + stepValues.employees), targetValues.employees),
              countries: Math.min(Math.floor(prev.countries + stepValues.countries), targetValues.countries),
              satisfaction: Math.min(Math.floor(prev.satisfaction + stepValues.satisfaction), targetValues.satisfaction)
            }));

            if (currentStep >= steps) {
              clearInterval(timer);
            }
          }, duration / steps);

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num;
  };

  const currentData = missionVisionData[activeTab];

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8f0f7 0%, #f5f8fb 50%, #ffffff 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #f8faff 0%, #e8f0f7 100%)',
        padding: '80px 20px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{
            fontSize: '72px',
            fontWeight: '700',
            marginBottom: '20px',
            color: '#2d3748'
          }}>
            Our <span style={{ color: '#4a90e2' }}>Mission</span>
          </h1>
          <p style={{
            fontSize: '20px',
            color: '#718096',
            marginBottom: '40px',
            maxWidth: '600px',
            margin: '0 auto 40px'
          }}>
            Business Presentation Template
          </p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '15px',
            background: 'white',
            padding: '15px 30px',
            borderRadius: '50px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '20px',
              fontWeight: 'bold'
            }}>F</div>
            <div style={{
              background: '#4a90e2',
              color: 'white',
              padding: '10px 25px',
              borderRadius: '30px',
              fontWeight: '600'
            }}>
              20 Total Asset
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '15px',
            justifyContent: 'center',
            marginTop: '40px'
          }}>
            <button
              onClick={() => setActiveTab('mission')}
              style={{
                padding: '12px 30px',
                borderRadius: '30px',
                border: 'none',
                background: activeTab === 'mission' ? '#4a90e2' : 'white',
                color: activeTab === 'mission' ? 'white' : '#4a90e2',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                fontSize: '16px'
              }}
            >
              <FaRocket style={{ marginRight: '8px' }} />
              Our Mission
            </button>
            <button
              onClick={() => setActiveTab('vision')}
              style={{
                padding: '12px 30px',
                borderRadius: '30px',
                border: 'none',
                background: activeTab === 'vision' ? '#667eea' : 'white',
                color: activeTab === 'vision' ? 'white' : '#667eea',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                fontSize: '16px'
              }}
            >
              <FaEye style={{ marginRight: '8px' }} />
              Our Vision
            </button>
          </div>
        </div>
      </div>

      {/* Grid Layout Section */}
      <div style={{ padding: '60px 20px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '30px',
          marginBottom: '40px'
        }}>
          {/* Company Mission Card */}
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '200px',
              background: 'url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: '0.9'
            }} />
            <div style={{ position: 'relative', zIndex: 1, marginTop: '160px' }}>
              <h3 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#2d3748',
                marginBottom: '15px'
              }}>
                Company Mission
              </h3>
              <p style={{
                color: '#718096',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                {currentData.description}
              </p>
              <div style={{
                width: '50px',
                height: '50px',
                background: '#e53e3e',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '20px'
              }}>
                📧
              </div>
            </div>
          </div>

          {/* Mission for Environment Card */}
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#2d3748',
              marginBottom: '30px'
            }}>
              Our Mission For Environment
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '20px'
            }}>
              {environmentalMissions.map((mission, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: mission.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '18px'
                  }}>
                    {mission.icon}
                  </div>
                  <span style={{
                    fontSize: '13px',
                    color: '#4a5568',
                    fontWeight: '500'
                  }}>
                    {mission.title}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Main Mission Slide */}
          <div style={{
            background: `linear-gradient(135deg, ${currentData.color} 0%, ${currentData.color}dd 100%)`,
            borderRadius: '20px',
            padding: '50px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%'
            }} />
            <h2 style={{
              fontSize: '48px',
              fontWeight: '700',
              marginBottom: '20px',
              position: 'relative'
            }}>
              {currentData.title}
            </h2>
            <p style={{
              fontSize: '16px',
              lineHeight: '1.7',
              opacity: '0.95',
              position: 'relative'
            }}>
              {currentData.description}
            </p>
          </div>

          {/* Start Your Mission Card */}
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '0',
              right: '0',
              width: '180px',
              height: '180px',
              background: 'url(https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&q=80)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '0 20px 0 100%',
              opacity: '0.9'
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#2d3748',
                marginBottom: '20px',
                maxWidth: '60%'
              }}>
                Start your mission from here
              </h3>
              <div style={{ marginTop: '60px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    fontSize: '13px',
                    color: '#718096',
                    marginBottom: '5px'
                  }}>Mission A</div>
                  <div style={{
                    height: '8px',
                    background: '#e2e8f0',
                    borderRadius: '10px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: '75%',
                      height: '100%',
                      background: '#4a90e2',
                      borderRadius: '10px'
                    }} />
                  </div>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    fontSize: '13px',
                    color: '#718096',
                    marginBottom: '5px'
                  }}>Mission B</div>
                  <div style={{
                    height: '8px',
                    background: '#e2e8f0',
                    borderRadius: '10px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: '60%',
                      height: '100%',
                      background: '#10b981',
                      borderRadius: '10px'
                    }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* About This Mission Card */}
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#2d3748',
              marginBottom: '30px'
            }}>
              About this mission
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '25px'
            }}>
              {['A', 'B'].map((letter, idx) => (
                <div key={idx}>
                  <div style={{
                    fontSize: '14px',
                    color: '#718096',
                    marginBottom: '15px',
                    fontWeight: '600'
                  }}>
                    Market Database {letter}
                  </div>
                  {['Goals 1', 'Goals 2', 'Goals 3'].map((goal, i) => (
                    <div key={i} style={{ marginBottom: '12px' }}>
                      <div style={{
                        fontSize: '12px',
                        color: '#a0aec0',
                        marginBottom: '5px'
                      }}>{goal}</div>
                      <div style={{
                        height: '6px',
                        background: '#e2e8f0',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${75 - i * 10}%`,
                          height: '100%',
                          background: idx === 0 ? '#4a90e2' : '#e53e3e',
                          borderRadius: '10px'
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Stats Card */}
          <div ref={sectionRef} style={{
            background: 'white',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '0',
              right: '0',
              width: '150px',
              height: '150px',
              background: 'url(https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&q=80)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '0 20px 0 50%',
              opacity: '0.85'
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4a90e2 0%, #667eea 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '32px',
                fontWeight: 'bold',
                marginBottom: '20px'
              }}>
                {animatedValues.satisfaction}%
              </div>
              <h4 style={{
                fontSize: '18px',
                color: '#2d3748',
                fontWeight: '600',
                marginBottom: '10px'
              }}>
                Customer Satisfaction
              </h4>
              <p style={{
                fontSize: '13px',
                color: '#718096',
                lineHeight: '1.6'
              }}>
                Over {formatNumber(animatedValues.companies)} companies trust our platform
              </p>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '25px',
          marginTop: '40px'
        }}>
          {currentData.features.map((feature, idx) => (
            <div key={idx} style={{
              background: 'white',
              borderRadius: '20px',
              padding: '35px',
              boxShadow: '0 5px 20px rgba(0,0,0,0.06)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.06)';
            }}>
              <div style={{
                width: '55px',
                height: '55px',
                borderRadius: '15px',
                background: `${currentData.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: currentData.color,
                fontSize: '24px',
                marginBottom: '20px'
              }}>
                {feature.icon}
              </div>
              <h4 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                {feature.title}
              </h4>
              <p style={{
                fontSize: '14px',
                color: '#718096',
                lineHeight: '1.6'
              }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div style={{
          marginTop: '60px',
          background: 'white',
          borderRadius: '25px',
          padding: '50px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#2d3748',
            marginBottom: '15px'
          }}>
            What Our Clients Say
          </h3>
          <p style={{
            color: '#718096',
            marginBottom: '40px'
          }}>
            Real stories from organizations we've helped transform
          </p>
          
          <div style={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
            <button
              onClick={prevTestimonial}
              style={{
                position: 'absolute',
                left: '-60px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '45px',
                height: '45px',
                borderRadius: '50%',
                border: 'none',
                background: currentData.color,
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
              }}
            >
              <FaChevronLeft />
            </button>

            <div style={{ padding: '0 20px' }}>
              <div style={{ marginBottom: '20px' }}>
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <FaStar key={i} style={{ color: '#fbbf24', fontSize: '20px', marginRight: '5px' }} />
                ))}
              </div>
              <p style={{
                fontSize: '18px',
                color: '#4a5568',
                fontStyle: 'italic',
                lineHeight: '1.8',
                marginBottom: '25px'
              }}>
                "{testimonials[currentTestimonial].content}"
              </p>
              <div>
                <div style={{
                  fontWeight: '700',
                  color: '#2d3748',
                  fontSize: '16px'
                }}>
                  {testimonials[currentTestimonial].name}
                </div>
                <div style={{
                  color: '#718096',
                  fontSize: '14px'
                }}>
                  {testimonials[currentTestimonial].role}, {testimonials[currentTestimonial].company}
                </div>
              </div>
            </div>

            <button
              onClick={nextTestimonial}
              style={{
                position: 'absolute',
                right: '-60px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '45px',
                height: '45px',
                borderRadius: '50%',
                border: 'none',
                background: currentData.color,
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
              }}
            >
              <FaChevronRight />
            </button>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            marginTop: '30px'
          }}>
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentTestimonial(idx)}
                style={{
                  width: idx === currentTestimonial ? '30px' : '10px',
                  height: '10px',
                  borderRadius: '5px',
                  border: 'none',
                  background: idx === currentTestimonial ? currentData.color : '#cbd5e0',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div style={{
          marginTop: '60px',
          background: `linear-gradient(135deg, ${currentData.color} 0%, ${currentData.color}dd 100%)`,
          borderRadius: '25px',
          padding: '60px',
          textAlign: 'center',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '300px',
            height: '300px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-100px',
            left: '-100px',
            width: '300px',
            height: '300px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%'
          }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{
              fontSize: '40px',
              fontWeight: '700',
              marginBottom: '20px'
            }}>
              Join Our Mission
            </h2>
            <p style={{
              fontSize: '18px',
              marginBottom: '35px',
              opacity: '0.95',
              maxWidth: '600px',
              margin: '0 auto 35px'
            }}>
              Ready to transform your HR operations and create exceptional employee experiences?
            </p>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button style={{
                padding: '15px 35px',
                borderRadius: '30px',
                border: 'none',
                background: 'white',
                color: currentData.color,
                fontWeight: '700',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                Start Your Journey <FaArrowRight />
              </button>
              <button style={{
                padding: '15px 35px',
                borderRadius: '30px',
                border: '2px solid white',
                background: 'transparent',
                color: 'white',
                fontWeight: '700',
                fontSize: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = currentData.color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'white';
              }}>
                Learn More About Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurMissionAndVision;